import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import useAuth from "../../hooks/useAuth";
import { isPlatformOperator } from "../../utils/authUtils";
import { listCashOnlyLedgers } from "../../api/accountApi";
import { getOnlineFeeConfig, listAllOnlineFeeConfigs, saveOnlineFeeConfig } from "../../api/onlineFeeApi";
import "../Users/UsersPage.css";

/**
 * "This Institute" tab: super-admin only, no real digicampusbd menu entry
 * for this either (the old app's ofps configuration screen is reached
 * through general settings, not a delegable sidebar item) — same
 * fixed-route pattern as BkashConfigPage. Unlike bKash, SSLCommerz
 * credentials are per-institute (OfpsAccountConfiguration), and once saved
 * cannot be overwritten from here (OfpsAccountConfigurationServiceImpl.save
 * rejects a second save — same "no accidental overwrite" behavior as the
 * old app). "All Institutes" tab: platform-operator only — see
 * AccessService.isPlatformOperator javadoc for why this isn't the same
 * `user.superAdmin` check as the tab-visibility gate below it used to be.
 *
 * Receipt Ledger picker uses `listCashOnlyLedgers` (Cash & Cash Equivalent
 * only, category_default_id {102}), not the generic unfiltered list — found
 * while sweeping Fee Management (see MIGRATION.md). The real old-app
 * template (`ssl-account-configuration.html`) calls the exact same
 * `/general/accounts/report/cash-equivalance/ledger/list` endpoint already
 * used by the fee-collection "Paid By" picker and Auto Ledger Setup.
 */
export default function OnlineFeeConfigPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("current");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const platformOperator = isPlatformOperator(user);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Online Fee Payment (SSLCommerz) Configuration</h1>
      </div>
      {platformOperator && (
        <Tabs
          tabs={[
            { key: "current", label: "This Institute" },
            { key: "all", label: "All Institutes" },
          ]}
          active={tab}
          onChange={setTab}
        />
      )}
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "current" && <CurrentInstituteTab setError={setError} setSuccess={setSuccess} />}
      {tab === "all" && platformOperator && <AllInstitutesTab setError={setError} />}
    </MainLayout>
  );
}

function CurrentInstituteTab({ setError, setSuccess }) {
  const [config, setConfig] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ledgers, setLedgers] = useState([]);
  const [form, setForm] = useState({ storeId: "", storePassword: "", ledgerId: "", requestId: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    setNotConfigured(false);
    getOnlineFeeConfig()
      .then(setConfig)
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setNotConfigured(true);
        } else {
          setError("Failed to load configuration.");
        }
      })
      .finally(() => setLoading(false));
    listCashOnlyLedgers().then(setLedgers);
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await saveOnlineFeeConfig({ ...form, ledgerId: Number(form.ledgerId) });
      setSuccess("SSLCommerz configuration saved.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!notConfigured && config) {
    return (
      <div>
        <h3>Current Configuration</h3>
        <ul>
          <li>Store ID: {config.storeId}</li>
          <li>Receipt Ledger: {config.ledgerName}</li>
          <li>Request ID: {config.requestId || "(none)"}</li>
          <li>Configured Since: {config.liveDate}</li>
        </ul>
        <p>Already configured — SSLCommerz credentials can't be changed from this screen once set.</p>
      </div>
    );
  }

  return (
    <>
      <p>No SSLCommerz account configured for this institute yet. Online fee payment is disabled until this is set up.</p>
      <form onSubmit={handleSubmit} className="user-form">
        <label>Store ID</label>
        <input value={form.storeId} onChange={handleChange("storeId")} required />
        <label>Store Password</label>
        <input type="password" value={form.storePassword} onChange={handleChange("storePassword")} required />
        <label>Receipt Ledger</label>
        <select value={form.ledgerId} onChange={handleChange("ledgerId")} required>
          <option value="">Select a ledger</option>
          {ledgers.map((l) => (
            <option key={l.ledgerId} value={l.ledgerId}>
              {l.ledgerName}
            </option>
          ))}
        </select>
        <label>Request ID (optional)</label>
        <input value={form.requestId} onChange={handleChange("requestId")} />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </form>
    </>
  );
}

function AllInstitutesTab({ setError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listAllOnlineFeeConfigs()
      .then(setRows)
      .catch(() => setError("Failed to load configurations."))
      .finally(() => setLoading(false));
  }, [setError]);

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <DataTable
      columns={[
        { key: "storeId", label: "Store ID" },
        { key: "ledgerName", label: "Receipt Ledger" },
        { key: "requestId", label: "Request ID" },
        { key: "liveDate", label: "Configured Since" },
      ]}
      rows={rows}
      emptyMessage="No institute has configured SSLCommerz yet."
    />
  );
}
